import { useEffect } from "react";
import { TileLayer, useMap } from "react-leaflet";
// The package's "browser" entry is a global script without exports; use the ES module source
import GoogleMutant from "leaflet.gridlayer.googlemutant/src/Leaflet.GoogleMutant.mjs";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Load the Google Maps JS API once and share the promise across all maps
let googleMapsPromise = null;
const loadGoogleMaps = () => {
  if (window.google?.maps) return Promise.resolve();
  if (!googleMapsPromise) {
    googleMapsPromise = new Promise((resolve, reject) => {
      const callbackName = "__sentinelGoogleMapsLoaded";
      window[callbackName] = () => {
        delete window[callbackName];
        resolve();
      };
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
        GOOGLE_MAPS_API_KEY
      )}&loading=async&callback=${callbackName}`;
      script.async = true;
      script.onerror = () => {
        googleMapsPromise = null;
        reject(new Error("Failed to load Google Maps"));
      };
      document.head.appendChild(script);
    });
  }
  return googleMapsPromise;
};

// Light gray style matching the CartoDB Positron look from design_guidelines.json
const GRAY_STYLE = [
  { stylers: [{ saturation: -100 }] },
  { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f2f2f2" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  { featureType: "administrative.land_parcel", elementType: "labels.text.fill", stylers: [{ color: "#bdbdbd" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#eeeeee" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e5e5e5" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dadada" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
  { featureType: "transit.line", elementType: "geometry", stylers: [{ color: "#e5e5e5" }] },
  { featureType: "transit.station", elementType: "geometry", stylers: [{ color: "#eeeeee" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9c9c9" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
];

const GoogleMutantLayer = ({ type }) => {
  const map = useMap();

  useEffect(() => {
    let layer = null;
    let cancelled = false;

    loadGoogleMaps()
      .then(() => {
        if (cancelled) return;
        layer = new GoogleMutant({
          type,
          maxZoom: 21,
          // Gray style only applies to the street map; satellite imagery stays as-is
          styles: type === "roadmap" ? GRAY_STYLE : [],
        });
        layer.addTo(map);
      })
      .catch((err) => console.error(err));

    return () => {
      cancelled = true;
      if (layer) map.removeLayer(layer);
    };
  }, [map, type]);

  return null;
};

// Base map layer: Google Maps when REACT_APP_GOOGLE_MAPS_API_KEY is set, grayscale OpenStreetMap otherwise.
// type: "roadmap" | "satellite" | "hybrid" | "terrain"
const GoogleTileLayer = ({ type = "roadmap" }) => {
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        className="map-tiles-gray"
      />
    );
  }
  return <GoogleMutantLayer type={type} />;
};

export default GoogleTileLayer;
